import type { CreativeProduct } from "@digital-twins/shared-types";
import { ProductProfile } from "./ProductProfile";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertCircle,
  Loader2,
  Plus,
  ArrowUpRight,
  Package,
} from "@digital-twins/design-system/icons";
import {
  CreativeEditorDialog,
  CreativeEditorSection,
} from "./CreativeEditorDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Text } from "@/components/ui/Text";
import { creativeLibraryApi } from "@/lib/api/creative-library";
import type { ProductManagerProps } from "@/types/creative-library";
export function ProductManager({
  brand,
  products,
  creatives = [],
  boards = [],
  onClose,
  onSaved,
  onUse,
}: ProductManagerProps) {
  const { t } = useTranslation("app");
  const [active, setActive] = useState<CreativeProduct | null>(null);
  const [name, setName] = useState("");
  const [sku, setSku] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const visibleProducts = products.filter(
    (product) => product.brandToken === brand?.token,
  );
  useEffect(() => {
    setActive(null);
    setName("");
    setSku("");
    setError(false);
  }, [brand?.token]);
  async function add() {
    if (!brand?.canEdit || !name.trim() || saving) return;
    setSaving(true);
    setError(false);
    try {
      const created = await creativeLibraryApi.createProduct({
        brandToken: brand.token,
        name: name.trim(),
        sku: sku.trim() || null,
      });
      setName("");
      setSku("");
      setActive(created);
      onSaved();
    } catch {
      setError(true);
    } finally {
      setSaving(false);
    }
  }
  if (brand && active)
    return (
      <ProductProfile
        key={active.token}
        product={
          products.find((product) => product.token === active.token) ?? active
        }
        brand={brand}
        creatives={creatives}
        boards={boards}
        onClose={() => setActive(null)}
        onSaved={onSaved}
        onUse={onUse}
      />
    );
  return (
    <CreativeEditorDialog
      open={Boolean(brand)}
      busy={saving}
      onClose={onClose}
      kind="products"
      title={t("creativeLibrary.products")}
      description={brand?.name ?? ""}
      testId="creative-products-dialog"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t("creativeLibrary.done")}
          </Button>
          {brand?.canEdit && (
            <Button
              type="submit"
              form="creative-product-form"
              variant="primary"
              className="gap-2"
              disabled={!name.trim() || saving}
              data-testid="creative-product-save"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              {t("creativeLibrary.addProduct")}
            </Button>
          )}
        </>
      }
    >
      <Text size="sm" color="muted">
        {t("creativeLibrary.productsHint")}
      </Text>
      {visibleProducts.length > 0 ? (
        <div className="flex flex-col gap-3">
          {visibleProducts.map((product) => (
            <div
              key={product.token}
              className="flex flex-col gap-3 rounded-xl border border-border bg-background p-4 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Package className="h-5 w-5" aria-hidden="true" />
                </div>
                <div className="flex min-w-0 flex-col gap-1">
                  <Text className="break-words">{product.name}</Text>
                  {product.sku && (
                    <Text size="xs" color="muted" className="break-words">
                      {product.sku}
                    </Text>
                  )}
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="shrink-0 gap-2 self-start sm:self-auto"
                onClick={() => setActive(product)}
                data-testid={`creative-use-product-${product.token}`}
              >
                {t("creativeLibrary.openProduct")}
                <ArrowUpRight className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex items-center gap-3 rounded-xl border border-dashed border-border bg-background p-4">
          <Package
            className="h-5 w-5 shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
          <Text size="sm" color="muted">
            {t("creativeLibrary.noProducts")}
          </Text>
        </div>
      )}
      {brand?.canEdit && (
        <form
          id="creative-product-form"
          onSubmit={(event) => {
            event.preventDefault();
            void add();
          }}
        >
          <CreativeEditorSection>
            <Text size="sm">{t("creativeLibrary.productDetails")}</Text>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-2">
                <Label htmlFor="product-name">
                  {t("creativeLibrary.productName")}
                </Label>
                <Input
                  autoFocus
                  id="product-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={255}
                  disabled={saving}
                  placeholder={t("creativeLibrary.productNamePlaceholder")}
                  data-testid="creative-product-name"
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="product-sku">{t("creativeLibrary.sku")}</Label>
                <Input
                  id="product-sku"
                  value={sku}
                  onChange={(e) => setSku(e.target.value)}
                  maxLength={255}
                  disabled={saving}
                  placeholder={t("creativeLibrary.productSkuPlaceholder")}
                  data-testid="creative-product-sku"
                />
              </div>
            </div>
            {error && (
              <Text
                size="sm"
                className="flex items-center gap-2 text-destructive"
                role="alert"
              >
                <AlertCircle className="h-4 w-4" />
                {t("creativeLibrary.saveFailed")}
              </Text>
            )}
          </CreativeEditorSection>
        </form>
      )}
    </CreativeEditorDialog>
  );
}
